type ErrorLike = {
  code?: string;
  message?: string;
  details?: unknown;
};

const codeMessageMap: Record<string, string> = {
  'auth/network-request-failed': 'Network error. Check your connection and try again.',
  'auth/too-many-requests': 'Too many attempts. Please wait a moment and try again.',
  'auth/internal-error': 'Something went wrong while signing in. Please try again.',
  'auth/invalid-credential': 'We could not sign you in with those credentials. Please try again.',
  'auth/wrong-password': 'We could not sign you in with those credentials. Please try again.',
  'auth/user-not-found': 'We could not sign you in with those credentials. Please try again.',
  'auth/invalid-email': 'Enter a valid email address.',
  'auth/email-already-in-use': 'We could not create an account with those credentials. Please try again.',
  'auth/credential-already-in-use': 'This sign-in method could not be used for this account. Try another option.',
  'auth/configuration-not-found': 'This sign-in option is not available right now.',
  'permission-denied': 'You do not have permission to do that.',
  'functions/permission-denied': 'You do not have permission to do that.',
  unauthenticated: 'Please sign in and try again.',
  'functions/unauthenticated': 'Please sign in and try again.',
  'auth/id-token-expired': 'Your session expired. Please sign in again.',
  'auth/user-token-expired': 'Your session expired. Please sign in again.',
  unavailable: 'Service is temporarily unavailable. Please try again shortly.',
  'functions/unavailable': 'Service is temporarily unavailable. Please try again shortly.',
  'deadline-exceeded': 'The request took too long. Please try again.',
  'functions/deadline-exceeded': 'The request took too long. Please try again.',
  'not-found': "This feature isn't available in this environment yet.",
  'functions/not-found': "This feature isn't available in this environment yet.",
};

const normalizeMessage = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  return value.trim();
};

export const humanizeAsyncError = (
  error: unknown,
  fallback = 'Something went wrong. Please try again.',
): string => {
  const maybeError = (error ?? {}) as ErrorLike;
  const code = normalizeMessage(maybeError.code).toLowerCase();
  const message = normalizeMessage(maybeError.message);
  const lowerMessage = message.toLowerCase();

  if (code && codeMessageMap[code]) {
    return codeMessageMap[code];
  }

  if (
    lowerMessage.includes('failed to fetch') ||
    lowerMessage.includes('network request failed') ||
    lowerMessage.includes('connection refused') ||
    lowerMessage.includes('network error')
  ) {
    return 'Network error. Check your connection and try again.';
  }

  if (lowerMessage.includes('timeout') || lowerMessage.includes('timed out')) {
    return 'The request timed out. Please try again.';
  }

  if (lowerMessage.includes('permission') || lowerMessage.includes('insufficient')) {
    return 'You do not have permission to do that.';
  }

  if (lowerMessage.includes('not-found') || lowerMessage.includes('not found')) {
    return "This feature isn't available in this environment yet.";
  }

  if (
    lowerMessage.includes('id token expired') ||
    lowerMessage.includes('token expired') ||
    lowerMessage.includes('session expired')
  ) {
    return 'Your session expired. Please sign in again.';
  }

  if (
    lowerMessage.includes('bad request') ||
    lowerMessage.includes('status code 400') ||
    lowerMessage.includes('status code 401') ||
    lowerMessage.includes('status code 403') ||
    lowerMessage.includes('status code 404') ||
    lowerMessage.includes('status code 500') ||
    lowerMessage.includes('internal error')
  ) {
    if (lowerMessage.includes('status code 401')) {
      return 'Your session expired. Please sign in again.';
    }
    return fallback;
  }

  return fallback;
};
